import React, { useState, useRef, useEffect, useMemo } from 'react'
import './SliderBar.css'
import { TextInput } from '../TextInput/TextInput'
import Icon from '../Icon2/Icon'
import Button from '../Button/Button'
import ButtonGroup from '../Button/ButtonGroup/ButtonGroup'
import Tooltip from '../Tooltip/Tooltip'

export interface labelsDescriptionArrayProps {
    label: string,
    description: string
}
export interface SliderProps {
    labelsDescriptionArray: Array<labelsDescriptionArrayProps>
    value?: number
    advanceSlider: boolean
    simpleSliderDefaultValue?: number
    onChange: (arg: number) => void
    discrete?: boolean
    start?: number
    end?: number
    step?: number
    fill?: string
    background?: string
    marks?: boolean
    markForEachStep?: boolean
    showValueTooltip?: boolean
    showDescription?: boolean
    makeLabelGLow?: boolean
    sliderInputGroup?: boolean
    sliderZoomInOutField?: boolean
    closeSliderOnOutsideClick?: boolean
    autoCloseTimeoutDuration?: number
    /**
     * Stepper + range: minus control, native `<input type="range">`, plus control, optional value readout.
     * Use for any bounded numeric setting (volume, opacity, spacing, etc.). Not tied to a specific domain.
     * Mutually exclusive with the advance slider track; set `advanceSlider={false}`.
     */
    sliderStepperRange?: boolean
    /**
     * Optional readout after the range (number, units, or custom node).
     * Prefer `(v) => …` so the label updates when `value` changes (e.g. while dragging the range).
     */
    valueLabel?: React.ReactNode | ((value: number) => React.ReactNode)
    testIdDecrement?: string
    testIdRange?: string
    testIdIncrement?: string
    testIdValueLabel?: string
    ariaLabelRange?: string
    decrementAriaLabel?: string
    incrementAriaLabel?: string
    isDisabled?: boolean
    onRangePointerDown?: React.PointerEventHandler<HTMLInputElement>
    onRangePointerUp?: React.PointerEventHandler<HTMLInputElement>
    /** When set, replaces built-in decrement stepping. */
    onDecrement?: () => void
    /** When set, replaces built-in increment stepping. */
    onIncrement?: () => void
    /** When set, replaces built-in range change handling (caller owns parsing / onChange). */
    onRangeInputChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
    /** Applied to numeric values from buttons and range when using built-in handlers. */
    roundValue?: (n: number) => number
    /**
     * If set, values strictly between `start` and `softFloor` are raised to `softFloor`
     * (applies to range drags and default decrement stepping).
     */
    softFloor?: number
    /**
     * When `enableIncrementFromMin` is true and the rounded value equals `start`,
     * the default increment handler emits this value instead of `start + step`
     * (useful when the first step above the minimum is not a simple arithmetic step).
     */
    incrementFromMinTarget?: number
    /** Turns on `incrementFromMinTarget` when stepping up from the minimum. */
    enableIncrementFromMin?: boolean
    decrementIcon?: string
    incrementIcon?: string
    stepperRangeClassName?: string
}

const SliderBar = (props: SliderProps) => {

    const { closeSliderOnOutsideClick = true, autoCloseTimeoutDuration = 4000 } = props;

    if (props.sliderInputGroup) {
        return <SliderWithInputField {...props} closeSliderOnOutsideClick={closeSliderOnOutsideClick} />
    }

    if (props.sliderZoomInOutField) {
        return <SliderWithZoomInOutField {...props} closeSliderOnOutsideClick={closeSliderOnOutsideClick} autoCloseTimeoutDuration={autoCloseTimeoutDuration} />
    }

    if (props.sliderStepperRange) {
        return <SliderStepperRange {...props} />
    }

    if (props.advanceSlider) {
        return <AdvanceSliderBar {...props} />
    } else {
        return <SimpleSliderBar {...props} />
    }
}

const SimpleSliderBar = (props: SliderProps) => {



    const { start = 0, end = 100, simpleSliderDefaultValue = 0, step = 1, fill = '#6C5CE7', background = "#C7B9FA", onChange } = props
    const [value, setValue] = useState(simpleSliderDefaultValue)

    const onSlide = event => {
        const newValue = event.target.value
        setValue(newValue)
        if (onChange) {
            onChange(newValue)
        }
    }

    const settings = {
        fill: fill || '#A9B6CB',
        background: background || '#EDF1F7',
    }

    const percentage = (100 * (value - start)) / (end - start)

    const bg = `linear-gradient(90deg, ${settings.fill} ${percentage}%, ${settings.background} ${percentage + 0.1
        }%)`

    return (
        <div className="SliderBar">
            <input
                type="range"
                min={start}
                max={end}
                className="SliderBar__track"
                style={{ background: bg, color: `${settings.fill}` }}
                value={value}
                onChange={onSlide}
                step={step}
            />
            <div className="SliderBar__numbers flex">
                <span className="SliderBar__start-number">{start}</span>
                <span className="SliderBar__start-number">{end}</span>
            </div>
        </div>
    )
}

const AdvanceSliderBar = (props: SliderProps) => {

    const { start = 0, end = 100, step = 1, onChange, value = 0, labelsDescriptionArray = [
        { label: "0", description: "" },
        { label: "100", description: "" }
    ], discrete = false, marks = false, markForEachStep = false, showValueTooltip = false, showDescription = false } = props

    const sliderRef = useRef<HTMLInputElement>(null)
    const [isHoveredOrActive, setIsHoveredOrActive] = useState(false);


    const labelDescriptionArray: labelsDescriptionArrayProps[] = labelsDescriptionArray;

    // Edge cases
    if (labelDescriptionArray.length <= 1) {
        // Return fallback UI
        return <div className='Advance-SlideBar-fallback-UI'>No objects in labels Array provided, Provide at least 2 objects</div>;
    }
    else if (step <= 0 || !step) {
        return <div className='Advance-SlideBar-fallback-UI'>Step value cannot be {step}, Provide a valid step value</div>;
    }
    else if (!Number.isFinite(start) || !Number.isFinite(end) || start >= end || start < 0 || end <= 0) {
        return <div className='Advance-SlideBar-fallback-UI'>{start === 0 ? null : `Start value cannot be ${start} and `} End value cannot be {end}, Provide a valid start and end value</div>;
    }

    const onSlide = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = Number(Math.round(Number(event.target.value)).toFixed(2));
        if (onChange) {
            onChange(newValue)
        }
    }

    const settings = {
        fill: props.fill || '#6C5CE7', //dark
        background: props.background || '#C7B9FA', //light
    }

    const percentage = (100 * (value - start)) / (end - start)

    const bg = `linear-gradient(90deg, ${settings.fill} ${percentage}%, ${settings.background} ${percentage + 0.1
        }%)`

    const newValueAndPositionLogicForLabelAndMark = (index: number): { newValue: number, newPosition: number } => {
        // Here we are calculating the value of each label and mark
        const newLabelAndMarksValueLogic: number = (end - start) / (labelDescriptionArray.length - 1);
        // Here we are calculating value with respect to the position of the label and mark
        const newLabelAndMarksValuePositionLogic: number = (start + (newLabelAndMarksValueLogic) * index);
        // Here we are rounding the value to the nearest integer
        const newValue: number = Number(Math.round(newLabelAndMarksValuePositionLogic));
        // Mapping the value to a percentage
        const newPosition: number = Number(Math.round((newLabelAndMarksValuePositionLogic / end) * 100));
        return { newValue, newPosition };
    }

    const handleMarkAndLabelClick = (index: number) => {
        const { newValue } = newValueAndPositionLogicForLabelAndMark(index);
        if (onChange) {
            onChange(newValue);
        }
    };

    const labelsMapper = labelDescriptionArray.map((item: labelsDescriptionArrayProps, index: number) => {
        const { newPosition } = newValueAndPositionLogicForLabelAndMark(index);
        const labelPosition = newPosition;
        return (
            <span
                key={index}
                className='Advance-SliderBar-labels'
                lang='en'
                style={{
                    left: `${(labelPosition).toFixed(2)}%`,
                    color: `${props.makeLabelGLow ? (labelPosition == value ? `${settings.fill}` : '#212121') : '#212121'}`,
                    fontWeight: `${props.makeLabelGLow ? (labelPosition == value ? '600' : 'normal') : 'normal'}`
                }}
                onClick={() => { handleMarkAndLabelClick(index) }}
            >
                {item.label}
            </span>
        )
    });

    const customThumbPositionLogicAndStyles = () => {
        const positionFromStartToValue = (value - start);
        const totalRange = (end - start);
        return {
            left: `${((positionFromStartToValue) / (totalRange)) * 100}%`,
            background: `${settings.fill}`,
        }
    };

    const discreteStep = (100 / (labelDescriptionArray.length - 1));
    const stepCalculator = (Number((discrete ? (discreteStep) : step).toFixed(2)));

    const handleMouseDown = () => {
        setIsHoveredOrActive(true);
        // Add new event listeners
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };

    const handleMouseMove = (e: MouseEvent) => {
        setIsHoveredOrActive(true);
        if (!sliderRef.current) {
            return;
        }
        // Get the slider element
        const slider = sliderRef.current;
        // Get the slider's bounding rectangle
        const rect = slider.getBoundingClientRect();
        // Calculate the x position of the mouse relative to the slider
        // here e.clientX is the x position of the mouse relative to the window
        // And rect.left is the x position of the slider relative to the window
        const x = e.clientX - rect.left;
        // Calculate the width of the slider
        const width = rect.right - rect.left;
        // Calculate the new value of the slider by converting the x position to a value within the range of the slider
        // here (x / width) gives the percentage of the slider that the mouse is over
        // and (end - start) gives the range of the slider
        // and ((end - start) * (x / width)) gives the value within the range of the slider
        // and start + ((end - start) * (x / width)) gives the value within the range of the slider starting from the start value
        let newValue = Number(Math.round(((x / width) * (end - start)) + start).toFixed(2));

        // This logic is used to snap the thumb to the nearest step
        if (discrete) {
            // Calculate the nearest step
            const stepInNumber = stepCalculator;
            // Calculate the remainder which is used to determine if the thumb should snap to the next step
            const remainder = newValue % stepInNumber;
            // If the remainder is less than half of the step, snap to the lower step, otherwise snap to the higher step
            if (remainder < stepInNumber / 2) {
                // Snap to the lower step if remainder is less than half of the step
                newValue = newValue - remainder;
            } else {
                // Snap to the higher step if remainder is greater than half of the step
                newValue = newValue + stepInNumber - remainder;
            }
        }
        //Ensure the value is within the range
        newValue = Math.round(Math.max(start, Math.min(end, newValue)));
        // Update the value
        if (onChange) {
            onChange(newValue);
        }
    };

    const handleMouseUp = () => {
        setIsHoveredOrActive(false);
        // Remove the event listeners
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
    };


    const marksMapper = labelDescriptionArray.map((_item: labelsDescriptionArrayProps, index: number) => {
        const { newPosition } = newValueAndPositionLogicForLabelAndMark(index);
        const markLeftPosition = newPosition;
        return (
            <span
                key={index}
                className='Advance-SliderBar-mark'
                style={{
                    left: `${(markLeftPosition).toFixed(2)}%`,
                    backgroundColor: markLeftPosition <= value ? props.background || '#c7b9fa' : props.fill || '#5d50bf'
                }}
                onClick={() => { handleMarkAndLabelClick(index) }}
            ></span>
        )
    });

    const range = (end - start);
    const totalSteps = range / stepCalculator;
    const stepsArray = Array.from({ length: totalSteps + 1 }, (_, i) => i * stepCalculator);
    const marksForEachStepMapper = stepsArray.map((currentStep) => {
        // Calculate the left position of the current mark as a percentage of the total range.
        // The Math.min function is used to ensure that the left position never exceeds 100%.
        const markLeftPosition = Math.min(((currentStep) / range) * 100, 100);
        const topValue = stepsArray.length <= 8 ? '-0.62rem' : stepsArray.length <= 50 ? '-0.62rem' : '-0.62rem';
        const heightValue = stepsArray.length <= 8 ? '0.188rem' : stepsArray.length <= 50 ? '0.188rem' : '0.188rem';
        const widthValue = stepsArray.length <= 8 ? '0.188rem' : stepsArray.length <= 50 ? '0.188rem' : '0.188rem'
        return (
            <span
                key={currentStep}
                id={currentStep.toString()}
                className='Advance-SliderBar-mark-for-each-step'
                style={{
                    top: `${topValue}`,
                    height: `${heightValue}`,
                    width: `${widthValue}`,
                    left: `${(markLeftPosition).toFixed(2)}%`,
                    backgroundColor: currentStep + start <= (value) ? props.background || '#c7b9fa' : props.fill || '#5d50bf'
                }}
                onClick={() => {
                    const newValue = currentStep + start;
                    if (onChange) {
                        onChange(newValue);
                    }
                }}
            ></span>
        )
    });


    const labelDescriptionMapper = () => {
        // Use 0 when value is undefined
        const valueToUse = value !== undefined ? value : 0;
        // Converting value to percentage but not multiplying by 100
        const valueToPercentage = (valueToUse / 100);
        // Multiplying by (labelDescriptionArray.length - 1)  then rounding it up to get the index of the labelDescriptionArray
        const indexToRenderDescriptionDynamically = Math.round(valueToPercentage * (labelDescriptionArray.length - 1));
        return (
            discrete ?
                <>
                    <span className='Advance-SliderBar-label-description'>
                        <span className='Advance-SliderBar-label-in-description'>
                            {labelDescriptionArray[(indexToRenderDescriptionDynamically)].label}
                        </span>
                        <span className='Advance-SliderBar-description'>
                            {labelDescriptionArray[(indexToRenderDescriptionDynamically)].description === '' ?
                                null
                                :
                                ':' + ' ' + labelDescriptionArray[(indexToRenderDescriptionDynamically)].description
                            }
                        </span>
                    </span>
                </>
                :
                null
        )
    };

    const handleMouseDownCombinedThumb = () => {
        setIsHoveredOrActive(true);
        handleMouseDown();
    }


    return (
        <div className='Advance-SliderBar-Wrapper'>
            <div className="Advance-SliderBar">
                <input
                    ref={sliderRef}
                    type="range"
                    min={start}
                    max={end}
                    className="Advance-SliderBar-track"
                    style={{ background: bg, color: `${settings.fill}` }}
                    value={value}
                    onChange={onSlide}
                    step={stepCalculator}
                />
                <div className='Advance-SliderBar-marks-wrapper'>
                    {marks && markForEachStep ?
                        marksForEachStepMapper
                        :
                        marks ?
                            marksMapper
                            :
                            null
                    }
                </div>
                <div className='Advance-SliderBar-custom-thumb-container'>
                    {/* Add ref={thumbRef} for access thumb in future */}
                    <span
                        data-testid='Advance-SliderBar-custom-thumb'
                        className='Advance-SliderBar-custom-thumb'
                        style={customThumbPositionLogicAndStyles()}
                        onMouseDown={handleMouseDownCombinedThumb}
                        onMouseEnter={() => setIsHoveredOrActive(true)}
                        onMouseLeave={() => setIsHoveredOrActive(false)} //Turn on if you want to hide tooltip on mouse leave
                    >
                        {
                            showValueTooltip ?
                                <span
                                    className={
                                        `Advance-SliderBar-custom-thumb-tooltip ${isHoveredOrActive ?
                                            'Advance-SliderBar-custom-thumb-tooltip-visible'
                                            :
                                            ''
                                        }`}
                                >
                                    {(Math.round(value)).toString()}
                                </span>
                                //This commented code is used to show the tooltip on mouse leave for debugging purposes
                                // <span
                                //     className='Advance-SliderBar-custom-thumb-tooltip Advance-SliderBar-custom-thumb-tooltip-visible'
                                // >
                                //     {(Math.round(value)).toString()}
                                // </span>
                                :
                                null
                        }
                    </span>
                </div>
                <div className="Advance-SliderBar-label-container">
                    {labelsMapper}
                </div>
                <div className="Advance-SliderBar-label-description-container">
                    {showDescription ?
                        labelDescriptionMapper()
                        :
                        null
                    }
                </div>
            </div>
        </div>
    )
}

const SliderWithInputField = (props) => {
    const [clickedInside, setClickedInside] = useState(false);
    const [val, setVal] = useState(props.value)
    const ref = useRef(null);

    const handleChange = (e) => {
        const numVal = e.target.value;
        if (/^\d*$/.test(numVal)) {
            const numericValue = Number(numVal);
            if (numericValue >= props.start && numericValue <= props.end) {
                setVal(numericValue);
            } else {
                setVal(props.start);
            }
        }
    };

    const handleClickOfField = () => {
        setClickedInside(true);
    }
    const handleHideDropdown = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
            if(props?.closeSliderOnOutsideClick){
                setClickedInside(false)
            }
        }
    }

    const handleClickOutside = (event: any) => {
        if (
            (ref.current && !ref.current.contains(event.target))
        ) {
            if(props?.closeSliderOnOutsideClick){
                setClickedInside(false)
            }
        }
    }

    useEffect(() => {
        if (props.onChange) {
            props?.onChange(val)
        }
    }, [val])

    useEffect(() => {
        setVal(props.value)
    }, [props.value])


    useEffect(() => {
        document.addEventListener('keydown', handleHideDropdown, true)
        document.addEventListener('click', handleClickOutside, true)
        return () => {
            document.removeEventListener('keydown', handleHideDropdown, true)
            document.removeEventListener('click', handleClickOutside, true)
        }
    })

    return (
        <div ref={ref}>
            {!clickedInside ? <div data-testid="Advance-Sliderbar-Field" className='Advance-Slidebar-Field' onClick={handleClickOfField}>
                <div>{props.value}%</div>
                <div className='Advance-Slidebar-Field-icon'><Icon version="v2" icon="CaretDown" size='medium' /></div>
            </div>
                : <div className='Advance-SliderBar-InputField'>
                    <div className='Advance-SliderBar-InputField-input'>
                        <TextInput
                            version="v2"
                            type="number"
                            placeholder={""}
                            width="medium"
                            maxLength={100}
                            min={props.start}
                            max={props.end}
                            step={props.step || 1}
                            onChange={handleChange}
                            value={val}
                        />
                    </div>
                    <div className='Advance-SliderBar-InputField-slider'>
                        <AdvanceSliderBar
                            {...props} />
                    </div>
                </div>
            }
        </div>
    )
}

const SliderWithZoomInOutField = (props) => {
    const [clickedInside, setClickedInside] = useState(false);
    const [val, setVal] = useState(props.value)
    const ref = useRef(null);

    const timeoutIdRef = useRef(null)

    const handleChange = (e) => {
        const numVal = e.target.value;
        if (/^\d*$/.test(numVal)) {
            const numericValue = Number(numVal);
            if (numericValue >= props.start && numericValue <= props.end) {
                setVal(numericValue);
            } else {
                setVal(props.start);
            }
        }
        cancelFadeOut()
    };

    const handleClickOfField = () => {
        setClickedInside(true);
        cancelFadeOut()
    }

    const handleHideDropdown = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
            if(props?.closeSliderOnOutsideClick){
                setClickedInside(false)
            }
        }
    }

    const handleClickOutside = (event: any) => {
        if (ref.current && !ref.current.contains(event.target)) {
            if(props?.closeSliderOnOutsideClick){
                setClickedInside(false)
            }
        }
    }

    useEffect(() => {
        if (props.onChange) {
            props?.onChange(val)
        }
    }, [val])

    useEffect(() => {
        setVal(props.value)
    }, [props.value])


    useEffect(() => {
        document.addEventListener('keydown', handleHideDropdown, true)
        document.addEventListener('click', handleClickOutside, true)
        return () => {
            document.removeEventListener('keydown', handleHideDropdown, true)
            document.removeEventListener('click', handleClickOutside, true)
        }
    })

    const clickedPlus = () => {
        setVal(prevVal => {
            const newVal = prevVal + props.step;
            if (newVal > props.end) {
                return props.end;
            }
            return newVal;
        }
        );
    }

    const clickedMinus = () => {
        setVal(prevVal => {
            const newVal = prevVal - props.step;
            if (newVal < props.start) {
                return props.start;
            }
            return newVal;
        }
        );
    }

    const startFadeOutTimer = () => {
        timeoutIdRef.current = setTimeout(() => {
            setClickedInside(false);
        }, props?.autoCloseTimeoutDuration); // default to 4 seconds
    };

    // Function to cancel the timeout on interaction
    const cancelFadeOut = async () => {
        await clearTimeout(timeoutIdRef.current)
        startFadeOutTimer();;
    };

    return (
        <div ref={ref} onClick={handleClickOfField}>
            <div data-testid='Advance-SliderBar-Zoom-Field' className='Advance-SlideBar-Zoom-Field'>
                <ButtonGroup>
                    <Tooltip content={`Zoom in`} position="top" showArrow={false}>
                        <Button
                            testId="cs-zoom-in-slider"
                            tabIndex={0}
                            onlyIcon={true}
                            onlyIconHoverColor="primary"
                            version="v2"
                            onClick={clickedPlus}
                            icon="v2-MagnifyingGlassPlus"
                        />
                    </Tooltip>
                    <Tooltip content={`Zoom out`} position="top" showArrow={false}>
                        <Button
                            testId="cs-zoom-in-slider"
                            tabIndex={0}
                            onlyIcon={true}
                            onlyIconHoverColor="primary"
                            version="v2"
                            icon="v2-MagnifyingGlassMinus"
                            onClick={clickedMinus}
                        />
                    </Tooltip>
                </ButtonGroup>
            </div>
            <div data-testid='Advance-SliderBar-Zoom-InputField' className={`Advance-SliderBar-InputField ${clickedInside ? 'show' : 'hide'}`}>
                <div className='Advance-SliderBar-InputField-input'>
                    <TextInput
                        version="v2"
                        type="number"
                        placeholder={""}
                        width="medium"
                        maxLength={100}
                        min={props.start}
                        max={props.end}
                        step={props.step || 1}
                        onChange={handleChange}
                        value={val}
                    />
                </div>
                <div data-testid='Advance-SliderBar-Zoom-InputField-slider' className='Advance-SliderBar-InputField-slider'>
                    <AdvanceSliderBar
                        {...props} />
                </div>
            </div>
        </div>
    )
}

const defaultRoundStepper = (n: number) => Math.round(Number(n))

const SliderStepperRange: React.FC<SliderProps> = props => {
    const {
        value = 0,
        onChange,
        start = 0,
        end = 100,
        step = 1,
        fill = '#6C5CE7',
        background = '#C7B9FA',
        valueLabel,
        isDisabled = false,
        roundValue = defaultRoundStepper,
        softFloor,
        incrementFromMinTarget,
        enableIncrementFromMin = false,
        onDecrement,
        onIncrement,
        onRangeInputChange,
        onRangePointerDown,
        onRangePointerUp,
        ariaLabelRange = 'Value',
        decrementAriaLabel = 'Decrease value',
        incrementAriaLabel = 'Increase value',
        testIdDecrement = 'cs-slider-stepper-decrement',
        testIdRange = 'cs-slider-stepper-range',
        testIdIncrement = 'cs-slider-stepper-increment',
        testIdValueLabel,
        decrementIcon = 'MinusSign',
        incrementIcon = 'Plus',
        stepperRangeClassName,
    } = props

    const trackSettings = useMemo(
        () => ({
            fill: fill || '#A9B6CB',
            background: background || '#EDF1F7',
        }),
        [fill, background],
    )

    const trackStyle = useMemo(() => {
        const range = end - start || 1
        const percentage = (100 * (value - start)) / range
        const bg = `linear-gradient(90deg, ${trackSettings.fill} ${percentage}%, ${trackSettings.background} ${percentage + 0.1}%)`
        return {
            background: bg,
            color: trackSettings.fill,
        } as React.CSSProperties
    }, [end, start, value, trackSettings])

    const resolvedValueLabel = useMemo(() => {
        if (valueLabel === undefined || valueLabel === null) {
            return null
        }
        if (typeof valueLabel === 'function') {
            return valueLabel(value)
        }
        return valueLabel
    }, [valueLabel, value])

    const handleDecrement = () => {
        if (isDisabled) return
        if (onDecrement) {
            onDecrement()
            return
        }
        const r = roundValue(value)
        const stepped = roundValue(r - step)
        const next =
            stepped <= start ? start : softFloor !== undefined ? Math.max(softFloor, stepped) : stepped
        onChange(next)
    }

    const handleIncrement = () => {
        if (isDisabled) return
        if (onIncrement) {
            onIncrement()
            return
        }
        const r = roundValue(value)
        if (
            enableIncrementFromMin &&
            incrementFromMinTarget !== undefined &&
            r === roundValue(start)
        ) {
            onChange(incrementFromMinTarget)
            return
        }
        const next = Math.min(end, roundValue(r + step))
        onChange(next)
    }

    const handleRangeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (isDisabled) return
        if (onRangeInputChange) {
            onRangeInputChange(e)
            return
        }
        let v = Number(e.target.value)
        if (softFloor !== undefined && v > start && v < softFloor) {
            v = softFloor
        }
        onChange(roundValue(v))
    }

    const rootClass = ['SliderBar-stepper-range', stepperRangeClassName].filter(Boolean).join(' ')

    return (
        <div className={rootClass}>
            <div className="SliderBar-stepper-range__row">
                <button
                    type="button"
                    className="SliderBar-stepper-range__btn"
                    aria-label={decrementAriaLabel}
                    data-test-id={testIdDecrement}
                    disabled={isDisabled}
                    onClick={handleDecrement}
                >
                    <Icon icon={decrementIcon} version="v2" size="small" />
                </button>
                <div className="SliderBar-stepper-range__track-wrap">
                    <input
                        type="range"
                        className="SliderBar__track SliderBar-stepper-range__range"
                        min={start}
                        max={end}
                        step={step}
                        value={value}
                        aria-label={ariaLabelRange}
                        aria-valuemin={start}
                        aria-valuemax={end}
                        aria-valuenow={value}
                        data-test-id={testIdRange}
                        disabled={isDisabled}
                        style={trackStyle}
                        onPointerDown={onRangePointerDown}
                        onPointerUp={onRangePointerUp}
                        onChange={handleRangeChange}
                    />
                </div>
                <button
                    type="button"
                    className="SliderBar-stepper-range__btn"
                    aria-label={incrementAriaLabel}
                    data-test-id={testIdIncrement}
                    disabled={isDisabled}
                    onClick={handleIncrement}
                >
                    <Icon icon={incrementIcon} version="v2" size="small" />
                </button>
                {resolvedValueLabel != null ? (
                    <span
                        className="SliderBar-stepper-range__value"
                        data-test-id={testIdValueLabel}
                    >
                        {resolvedValueLabel}
                    </span>
                ) : null}
            </div>
        </div>
    )
}

SliderBar.defaultProps = {
    labelsDescriptionArray: [
        { label: "0", description: "" },
        { label: "100", description: "" }
    ],
    start: 0,
    end: 100,
    fill: '#6C5CE7',
    background: '#C7B9FA',
    step: 1,
    discrete: false,
    marks: false,
    markForEachStep: false,
    showValueTooltip: false,
    showDescription: false,
    value: 0,
} as Partial<SliderProps>

export default SliderBar